import {
  DesignExpressionFunctions,
  GetBuildingsFn,
  GetStoreyFn,
  StoreyExpressionVariables,
  StoreySummaryVariables,
  type ActivityExpressionVariablesRecord,
  type BuildingExpressionVariables,
} from '@shared/models/design/design-expression-variables.model';
import { ActivityId } from '@shared/models/activity-class.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { toActivityType } from '../migration';
import { selectByIndexesOrNames } from '@shared/helpers/array.helpers';
import {
  combineStoreySummaryVariables,
  getStackGfa,
  getStoreySumFromDesignExpressionVariables,
  getStoreysCountFromStacks,
  resolveStoreySelectors,
  toGetStoreySummary,
  withPartyWallGwa,
} from './storey-sum-from-design.helpers';
import { toInternalGetStoreySelectors } from './get-storey-index.helpers';

/**
 * One building's storey stack as seen by {@link GetBuildingsFn}: selectable by
 * index, name, or id.
 */
export type BuildingExpressionStack = {
  id: string;
  name: string;
  storeys: StoreyExpressionVariables[];
};

type GetDesignExpressionFunctionsParams = {
  programs: ActivityExpressionVariablesRecord;
  /**
   * Per-building stacks for {@link GetStoreyFn} and {@link GetBuildingsFn}.
   * Order matches the design. `getStorey(n)` takes floor `n` from every stack.
   */
  buildings: BuildingExpressionStack[];
  /**
   * Site-wide summary used when `getStorey()` / `getBuildings()` has no
   * selectors (GFA/perimeter across every building).
   */
  siteSummary: StoreySummaryVariables;
  largestActivityType: ActivityType;
};

/**
 * Expression functions (`getStorey`, `getBuildings`, `isActivity`) for a design.
 * Numeric `getStorey(n)` is 1-based and takes that floor from every building.
 * @param params.programs Activity mix used by the CALCULATIONS storey sum
 * @param params.buildings Per-building stacks
 * @param params.siteSummary Site-wide totals for empty selectors
 * @param params.largestActivityType Activity type used by {@link createIsActivityFn}
 */
export const getDesignExpressionFunctions = ({
  programs,
  buildings,
  siteSummary,
  largestActivityType,
}: GetDesignExpressionFunctionsParams): DesignExpressionFunctions => {
  const siteGfa = buildings.reduce(
    (acc, building) => acc + getStackGfa(building.storeys),
    0,
  );
  return {
    getStorey: createGetStoreyForBuildingsFn(
      buildings,
      programs,
      siteSummary,
      siteGfa,
    ),
    getBuildings: createGetBuildingsFn(
      buildings,
      programs,
      siteSummary,
      siteGfa,
    ),
    isActivity: createIsActivityFn(largestActivityType),
  };
};

/**
 * Select buildings by 1-based index, name, or id.
 * No selectors returns the same sums as {@link GetStoreyFn} with no selectors.
 */
const createGetBuildingsFn = (
  buildings: BuildingExpressionStack[],
  programs: ActivityExpressionVariablesRecord,
  siteSummary: StoreySummaryVariables,
  siteGfa: number,
): GetBuildingsFn => {
  return (...selectors: Array<number | string>) => {
    const selected =
      selectors.length === 0
        ? buildings
        : selectByIndexesOrNames(buildings, selectors, { indexBase: 1 });
    const summary =
      selectors.length === 0
        ? siteSummary
        : combineStoreySummaryVariables(
            selected.map((building) =>
              withPartyWallGwa(
                toGetStoreySummary(
                  getStoreySumFromDesignExpressionVariables(
                    { storeys: building.storeys, programs },
                    [],
                    { siteGfa },
                  ),
                ),
                building.storeys,
              ),
            ),
          );
    return toBuildingExpressionVariables(summary, selected, programs, siteGfa);
  };
};

/**
 * Attach a display name, floor count, and a {@link GetStoreyFn} scoped to the
 * selected stacks. Nested `getStorey` matches the site-level function name.
 */
const toBuildingExpressionVariables = (
  summary: StoreySummaryVariables,
  buildings: BuildingExpressionStack[],
  programs: ActivityExpressionVariablesRecord,
  siteGfa: number,
): BuildingExpressionVariables => ({
  ...summary,
  name: joinBuildingNames(buildings),
  storeys_count: getStoreysCountFromStacks(
    buildings.map((building) => building.storeys),
  ),
  getStorey: createGetStoreyForBuildingsFn(
    buildings,
    programs,
    summary,
    siteGfa,
  ),
});

/**
 * Storey selector scoped to the given buildings. No selectors returns
 * {@link allStoreysSummary}. Numeric indexes are 1-based. Each building
 * contributes matching floors; buildings with no match are skipped so an
 * empty stack cannot overwrite a sibling's elevation.
 */
const createGetStoreyForBuildingsFn = (
  buildings: BuildingExpressionStack[],
  programs: ActivityExpressionVariablesRecord,
  allStoreysSummary: StoreySummaryVariables,
  siteGfa: number,
): GetStoreyFn => {
  return (...selectors) => {
    if (selectors.length === 0) return allStoreysSummary;
    const resolved = resolveStoreySelectors(selectors);
    if (resolved.length === 0) return allStoreysSummary;
    const internalSelectors = toInternalGetStoreySelectors(resolved);
    const perBuilding = buildings.flatMap((building) => {
      const selected = selectByIndexesOrNames(
        building.storeys,
        internalSelectors,
      );
      if (selected.length === 0) return [];
      return [
        withPartyWallGwa(
          toGetStoreySummary(
            getStoreySumFromDesignExpressionVariables(
              { storeys: building.storeys, programs },
              internalSelectors,
              { siteGfa },
            ),
          ),
          selected,
        ),
      ];
    });
    return combineStoreySummaryVariables(perBuilding);
  };
};

/**
 * Single building keeps its label; several are joined so the result still has
 * a {@link BuildingExpressionVariables.name}.
 */
const joinBuildingNames = (buildings: BuildingExpressionStack[]): string => {
  if (buildings.length === 1) return buildings[0]?.name ?? '';
  return buildings.map((building) => building.name).join(', ');
};

/**
 * Returns 1 if `idOrType` is the building's largest program.
 * Legacy {@link ActivityId.PrivateHousing} maps to {@link ActivityType.Residential}.
 */
export const createIsActivityFn = (
  activityTypeIdOrType: ActivityId | ActivityType,
): DesignExpressionFunctions['isActivity'] => {
  const designActivityType = toActivityType(activityTypeIdOrType);
  return (idOrType: ActivityId | ActivityType) => {
    const type = toActivityType(idOrType);
    return type === designActivityType ? 1 : 0;
  };
};
